// Custom cutting boards with a logo. Targets (SERPs checked 2026-11-03):
// custom cutting boards with logo 210 and cutting boards with logo 210 (KD 0,
// $9), plus personalized cutting board 14,800, whose #1 result is a logo
// client-gift page. Also engraved cutting board 3,600, custom cutting board 880. SERPs
// are Etsy and gift shops; this page leads with bulk logo boards (realtor
// closing gifts, client gifts) and keeps one-off personal boards.
const {
  engraving,
  quoted,
  setup,
  each,
  medium,
  costAnswer,
  timingAnswer,
  minimumAnswer,
  ownItemsAnswer,
  shipAnswer,
  engravingItem,
} = require('./shared')

module.exports = engravingItem({
  key: 'cuttingBoards',
  slug: 'cutting-boards',
  path: '/services/laser-engraving/cutting-boards/',
  noun: 'board',
  crumb: 'Cutting boards',
  title: 'Custom Cutting Boards with Your Logo | PrintAWorld',
  description:
    'Custom cutting boards with your logo, laser engraved in NYC for client and closing gifts, plus personalized boards with a name or a recipe.',
  eyebrow: 'Cutting boards / laser engraved in New York',
  heading:
    'Custom cutting boards with your logo, and personalized boards with a name.',
  answer: `PrintAWorld laser-engraves custom cutting boards with your logo in New York, for client gifts, realtor closing gifts and restaurants, and personalized boards with a family name, a wedding date or a handwritten recipe. We engrave walnut, maple, cherry and bamboo, end grain or edge grain. Engraving is a ${setup} setup plus ${each(medium, 'board')}; the boards are ${quoted.toLowerCase()}, or send your own.`,
  cta: 'Get a board quote',
  heroImage: 'logoStack',
  ideaImages: ['familyBoard', 'recipeBoard', 'charcuterie'],
  images: {
    familyBoard: {
      file: 'family-board',
      alt: 'Walnut cutting board engraved The Carters, Est. 2019 with a leaf border, on a kitchen counter',
      caption: 'A family name on walnut',
    },
    logoStack: {
      file: 'logo-board-stack',
      alt: 'Stack of maple cutting boards each engraved with the same real estate company logo',
      caption: 'Logo boards for closing gifts',
    },
    recipeBoard: {
      file: 'recipe-board',
      alt: 'Maple board engraved with a handwritten-style apple pie recipe',
      caption: 'A family recipe, in their handwriting',
    },
    charcuterie: {
      file: 'charcuterie-names',
      alt: 'Long walnut serving board engraved with a couple’s names and wedding date, set with cheese',
      caption: 'A serving board for a wedding',
    },
  },
  intro: {
    eyebrow: 'A gift that stays on the counter',
    title: 'Personalized and logo cutting boards, engraved into the wood.',
    paragraphs: [
      'The laser burns the design into the wood itself, with no ink, so it stays as long as the board does. Dark walnut engraves subtly; maple and bamboo give the most contrast.',
      'Families order one with a name and a date. Realtors, banks and companies order them by the dozen with a logo for closing and client gifts.',
    ],
  },
  ideas: {
    eyebrow: 'Cutting board ideas',
    title: 'Engraved boards people use and display.',
  },
  uses: {
    eyebrow: 'Who orders them',
    title: 'Closing gifts, wedding gifts and family boards.',
    items: [
      {
        label: 'Realtors',
        title: 'Closing gifts',
        body: 'Your logo plus the family’s name and new address, one board per closing.',
      },
      {
        label: 'Companies',
        title: 'Client gifts',
        body: 'A logo board in a gift box for the holidays, ordered in one run.',
      },
      {
        label: 'Weddings',
        title: 'Names and the date',
        body: 'A serving board with the couple’s names, the date or the venue.',
      },
      {
        label: 'Families',
        title: 'Recipes and keepsakes',
        body: 'A grandparent’s handwritten recipe, from a photo of the card.',
      },
      {
        label: 'Restaurants',
        title: 'Branded serving boards',
        body: 'Your restaurant’s logo on the boards you serve on.',
      },
    ],
  },
  materials: {
    link: 'Which woods',
    eyebrow: 'Woods',
    title: 'Walnut, maple, cherry and bamboo boards.',
    items: [
      {
        title: 'Maple',
        body: 'Light wood that engraves dark brown: the clearest names and logos.',
      },
      {
        title: 'Walnut',
        body: 'Rich, dark wood with a subtle, elegant engraving.',
      },
      {
        title: 'Cherry and bamboo',
        body: 'Warm cherry and hard bamboo, both good for crisp detail.',
      },
      {
        title: 'Your own boards',
        body: `Send boards you have. We engrave areas up to ${engraving.workAreas.wood}, after testing one.`,
      },
    ],
  },
  process: {
    title: 'From your design to a finished board.',
    note: 'Standard lead time is 2–3 weeks, plus shipping. For holiday or closing gifts, tell us the date.',
    steps: [
      {
        title: 'Send the design',
        body: 'The names, the recipe photo or your logo, the wood and the count.',
      },
      {
        title: 'Approve a proof',
        body: 'We lay out the engraving on the board and send a proof.',
      },
      {
        title: 'We engrave',
        body: 'Each board is engraved, cleaned and oiled with food-safe oil.',
      },
      {
        title: 'Wrap and ship',
        body: 'Gift wrapped if you want, shipped anywhere in the U.S. or picked up in NYC.',
      },
    ],
  },
  pricing: {
    title: 'Engraved cutting board pricing, per board.',
    cta: 'Price my boards',
    rows: [
      { label: 'Engraving, per board', value: medium },
      { label: 'The boards', value: quoted },
      { label: 'Engraving both sides', value: quoted },
      { label: 'Gift boxes', value: quoted },
      { label: 'Rush', value: 'Case by case' },
    ],
  },
  bulk: {
    eyebrow: 'Cutting boards in bulk',
    title: 'Logo boards for every client, with a name on each.',
    paragraphs: [
      'For bulk orders we engrave one board for your approval, then the run. Each board can carry its own name or address from your spreadsheet.',
      'Reorder the same design all year, one closing at a time or a batch each quarter.',
    ],
    cta: 'Plan a bulk order',
  },
  faqTitle: 'Engraved cutting board questions, answered.',
  faqs: [
    {
      question: 'How much does an engraved cutting board cost?',
      answer: costAnswer(
        medium,
        `The board itself, engraving both sides and gift boxes are ${quoted.toLowerCase()}.`
      ),
    },
    {
      question: 'Is an engraved cutting board food safe?',
      answer:
        'The engraving is burned into the wood, with no ink or paint. We finish boards with food-safe oil. For boards used for daily chopping, put the design on the back or near an edge.',
    },
    {
      question: 'Can you engrave a handwritten recipe?',
      answer:
        'Yes. Send a clear photo of the recipe card, flat and in good light. We clean it up and engrave it in the original handwriting.',
    },
    {
      question: 'Can you engrave a board I already have?',
      answer: ownItemsAnswer('board'),
    },
    {
      question: 'Is there a minimum order?',
      answer: minimumAnswer('board'),
    },
    {
      question: 'How long does it take?',
      answer: timingAnswer('For the holidays, order in November.'),
    },
    {
      question: 'Do you ship?',
      answer: shipAnswer,
    },
  ],
  quote: {
    title: 'Tell us about your cutting boards.',
    body: 'Send the names, recipe or logo, the wood, how many and when you need them. We’ll reply with a proof plan and a written quote.',
    checklist: [
      'The names, a recipe photo or your logo',
      'The wood and size you want, or your own boards',
      'How many, with a names list if each differs',
      'Your in-hand date and delivery ZIP',
    ],
  },
  moreCards: ['corporateGifts', 'tumblers', 'christmasOrnaments'],
  schema: {
    name: 'Custom Cutting Boards with Logo',
    alternateName: [
      'Personalized cutting boards',
      'Custom cutting boards',
      'Engraved cutting boards with logo',
      'Realtor closing gift cutting boards',
    ],
    serviceType: 'Laser-engraved cutting boards',
  },
})
